/**
 * Code Mapping Utilities
 * Maps between INPUT code (clean source) and OUTPUT HTML (rendered with insights)
 *
 * Architecture:
 * - INPUT code is source of truth for semantics
 * - OUTPUT HTML is rendering layer with line numbers matching INPUT
 * - Line numbers provide 1:1 mapping between layers
 *
 * @module codeMapping
 */

export interface CodePosition {
  lineNumber: number;       // 1-indexed (matches HTML display)
  columnStart: number;      // Character offset in line (0-indexed)
  columnEnd: number;        // End of token (exclusive)
  tokenText: string;        // Actual token text (e.g., "async")
}

export interface InputCodeContext {
  token: string;            // Token text
  lineNumber: number;       // 1-indexed line number
  currentLine: string;      // The line containing the token
  previousLines: string[];  // Lines before current (ordered oldest to newest)
  nextLines: string[];      // Lines after current (ordered)
  fullContext: string;      // All lines joined with \n
}

export interface InsightData {
  surface: string;
  concept: string;
  paradigm: string;
  badge?: string;
}

/**
 * Maps an HTML element from the rendered output to its position in the INPUT code
 *
 * Strategy:
 * 1. Find parent line container in HTML
 * 2. Extract line number from line number span
 * 3. Extract token text from element
 * 4. Find token position in INPUT code line using string search
 *
 * @param element - The HTML element (typically a <span> token)
 * @param code - The original INPUT code string
 * @returns Position in input code, or null if mapping fails
 */
export function getCodePositionFromElement(
  element: Element,
  code: string
): CodePosition | null {
  // Validate element has content
  const tokenText = element.textContent?.trim();
  if (!tokenText || tokenText.length === 0) {
    console.warn('[codeMapping] Element has no text content');
    return null;
  }

  // Find parent line container
  const lineContainer = element.closest('.flex.items-center');
  if (!lineContainer) {
    console.warn('[codeMapping] Element not in line container');
    return null;
  }

  // Extract line number from HTML
  const lineNumSpan = lineContainer.querySelector('.text-gray-500.w-8');
  const lineNumberText = lineNumSpan?.textContent?.trim();
  if (!lineNumberText) {
    console.warn('[codeMapping] Line number not found');
    return null;
  }

  const lineNumber = parseInt(lineNumberText, 10);
  if (isNaN(lineNumber) || lineNumber < 1) {
    console.warn('[codeMapping] Invalid line number:', lineNumberText);
    return null;
  }

  // Get the line from INPUT code
  const lines = code.split('\n');
  const lineIndex = lineNumber - 1; // Convert to 0-indexed

  if (lineIndex < 0 || lineIndex >= lines.length) {
    console.warn('[codeMapping] Line number out of bounds:', lineNumber, 'total lines:', lines.length);
    return null;
  }

  const inputLine = lines[lineIndex];

  // Find token position in INPUT line
  const columnStart = inputLine.indexOf(tokenText);

  if (columnStart === -1) {
    // Token not found - could be due to HTML formatting differences
    console.warn('[codeMapping] Token not found in input line:', {
      token: tokenText,
      line: inputLine,
      lineNumber
    });

    // Fallback: Return position at start of line
    return {
      lineNumber,
      columnStart: 0,
      columnEnd: tokenText.length,
      tokenText
    };
  }

  return {
    lineNumber,
    columnStart,
    columnEnd: columnStart + tokenText.length,
    tokenText
  };
}

/**
 * Extracts context from INPUT code around a specific position
 *
 * Strategy:
 * 1. Split code into lines
 * 2. Extract ±N lines around target line
 * 3. Handle edge cases (first/last lines)
 * 4. Return structured context for prompt generation
 *
 * @param code - The original INPUT code string
 * @param position - The position to extract context for
 * @param contextSize - Number of lines before/after to include (default: 2)
 * @returns Structured context with previous/current/next lines
 */
export function getInputCodeContext(
  code: string,
  position: CodePosition,
  contextSize: number = 2
): InputCodeContext {
  const lines = code.split('\n');
  const lineIndex = position.lineNumber - 1; // Convert to 0-indexed

  // Validate line index
  if (lineIndex < 0 || lineIndex >= lines.length) {
    throw new Error(`Line number ${position.lineNumber} out of bounds (total lines: ${lines.length})`);
  }

  // Calculate context boundaries
  const startIndex = Math.max(0, lineIndex - contextSize);
  const endIndex = Math.min(lines.length - 1, lineIndex + contextSize);

  // Extract context slices
  const previousLines = lines.slice(startIndex, lineIndex);
  const currentLine = lines[lineIndex];
  const nextLines = lines.slice(lineIndex + 1, endIndex + 1);

  // Join all lines for full context
  const fullContext = [
    ...previousLines,
    currentLine,
    ...nextLines
  ].join('\n');

  return {
    token: position.tokenText,
    lineNumber: position.lineNumber,
    currentLine,
    previousLines,
    nextLines,
    fullContext
  };
}

/**
 * Stitches a generated insight back into the OUTPUT HTML
 *
 * Strategy:
 * 1. Parse HTML string into DOM
 * 2. Find line container matching position.lineNumber
 * 3. Find span element with matching token text
 * 4. Inject data-* attributes
 * 5. Add 'hoverable' class
 * 6. Serialize back to HTML string
 *
 * @param html - The current OUTPUT HTML string
 * @param position - Position where insight should be injected
 * @param insight - The insight data to inject
 * @returns Updated HTML string with insight injected
 */
export function stitchInsightIntoHTML(
  html: string,
  position: CodePosition,
  insight: InsightData
): string {
  // Parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all line containers
  const lineContainers = doc.querySelectorAll('.flex.items-center');

  let found = false;

  // Search for matching line number
  for (const container of Array.from(lineContainers)) {
    const lineNumSpan = container.querySelector('.text-gray-500.w-8');
    const lineNumText = lineNumSpan?.textContent?.trim();

    if (!lineNumText) continue;

    const lineNum = parseInt(lineNumText, 10);

    if (lineNum === position.lineNumber) {
      // Found the right line - now find the token
      const contentDiv = container.querySelector('.flex-1');
      if (!contentDiv) continue;

      const tokenSpans = contentDiv.querySelectorAll('span');

      // Search for first span with matching text
      for (const span of Array.from(tokenSpans)) {
        const spanText = span.textContent?.trim();

        if (spanText === position.tokenText) {
          // Found the token - inject attributes
          span.setAttribute('data-surface', insight.surface);
          span.setAttribute('data-concept', insight.concept);
          span.setAttribute('data-paradigm', insight.paradigm);

          if (insight.badge) {
            span.setAttribute('data-badge', insight.badge);
          }

          // Add hoverable class if not already present
          if (!span.classList.contains('hoverable')) {
            span.classList.add('hoverable');
          }

          found = true;
          break; // Stop after first match in line
        }
      }

      if (found) break; // Stop after finding the line
    }
  }

  if (!found) {
    console.warn('[codeMapping] Failed to stitch insight - token not found in HTML:', {
      lineNumber: position.lineNumber,
      token: position.tokenText
    });
  }

  // Serialize back to HTML
  return doc.body.innerHTML;
}

/**
 * Generates a cache key for dynamic insights
 *
 * Strategy:
 * - Combine language pair, token, line number, and context hash
 * - Ensures uniqueness across different contexts
 * - Includes language pair for proper invalidation
 *
 * @param token - The token text
 * @param context - The code context
 * @param sourceLanguage - Source language
 * @param targetLanguage - Target language
 * @returns Cache key string
 */
export function generateInsightCacheKey(
  token: string,
  context: InputCodeContext,
  sourceLanguage: string,
  targetLanguage: string
): string {
  // Create stable hash of context
  const contextStr = [
    context.previousLines.join('\n'),
    context.currentLine,
    context.nextLines.join('\n')
  ].join('\n');

  const contextHash = simpleHash(contextStr);

  return `insight:${sourceLanguage}→${targetLanguage}:${token}:L${context.lineNumber}:${contextHash}`;
}

/**
 * Simple hash function for string content
 * (For production, consider crypto.subtle.digest for better distribution)
 *
 * @param str - String to hash
 * @returns Hash as base36 string
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Validates if a token is suitable for dynamic insight generation
 *
 * Filters out:
 * - Empty/whitespace tokens
 * - Single character operators that are too generic
 * - Line numbers
 *
 * @param element - The HTML element
 * @returns True if token is valid for insight generation
 */
export function isValidInsightToken(element: Element): boolean {
  const text = element.textContent?.trim();

  if (!text || text.length === 0) {
    return false;
  }

  // Filter out line numbers
  if (element.classList.contains('text-gray-500') &&
      element.classList.contains('w-8')) {
    return false;
  }

  // Allow multi-character tokens
  if (text.length >= 2) {
    return true;
  }

  // Allow single-character if it's a keyword-like character
  // (Allow operators like *, =, etc. as they can have language differences)
  const validSingleChars = /[a-zA-Z0-9_$@#]/;
  return validSingleChars.test(text);
}
